import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { IdProps } from '~/components/ChatAvatar/Canvas/Character/RobotCharacter';
import { SECOND } from '~/constants';
import { useCommonChatStore } from '~/store';
import useChatAvatarStore from '~/store/useChatAvatarStore';

interface useAnimationProps {
  scene?: THREE.Object3D;
  id: IdProps['id'];
}

const useAnimation = ({ scene, id }: useAnimationProps) => {
  const mixer = useRef<THREE.AnimationMixer>();
  const cachedAnimation = useRef<THREE.AnimationClip>();

  const animationState = useChatAvatarStore(
    (state) => state.character?.[id]?.animationState,
  );
  const currentAnimation = useChatAvatarStore(
    (state) => state.character?.[id]?.currentAnimation,
  );
  const animationList = useChatAvatarStore(
    (state) => state.character?.[id]?.animations,
  );

  const { setCurrentAnimation, setAnimationState } = useChatAvatarStore(
    (state) => ({
      setCurrentAnimation: state.setCurrentAnimation,
      setAnimationState: state.setAnimationState,
    }),
  );

  const avatarInfo = useCommonChatStore((state) => ({
    avatarInfo: state.avatarInfo,
  }));
  const findAvatarById = (id: string) => {
    return (
      avatarInfo.avatarInfo &&
      avatarInfo.avatarInfo.find((info) => info.id === id)
    );
  };
  const avatar = findAvatarById(id);

  useEffect(() => {
    if (!scene) return;
    mixer.current = new THREE.AnimationMixer(scene);
  }, [scene]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const filterScaleTracks = (clip: THREE.AnimationClip) => {
      const filteredTracks = clip.tracks.filter(
        (track) => !track.name.endsWith('.scale'),
      );
      return new THREE.AnimationClip(clip.name, clip.duration, filteredTracks);
    };

    const setAnimation = async () => {
      const type = animationState?.type;
      if (type === 'Idle') {
        try {
          if (!avatar || !avatar.idle_motion_path) return;

          let target: THREE.AnimationClip;

          if (!cachedAnimation.current) {
            const loader = new GLTFLoader();
            const anim = await loader.loadAsync(avatar.idle_motion_path);
            const rawClip = anim?.animations[0];
            if (!rawClip) return;

            const filteredClip = filterScaleTracks(rawClip); // 🔸 스케일 트랙 제거
            target = filteredClip;
            cachedAnimation.current = target;
          } else {
            target = cachedAnimation.current;
          }

          if (target) {
            setCurrentAnimation({ id, animation: target });
            timeout = setTimeout(() => {
              setAnimationState({
                id,
                state: {
                  type: 'Idle',
                },
              });
            }, target.duration * SECOND);
          }
        } finally {
          //
        }
      }
      if (type === 'external') {
        try {
          const loader = new GLTFLoader();
          const anim = await loader.loadAsync(animationState.animationName);
          const rawClip = anim?.animations[0];
          if (!rawClip) return;

          const filteredClip = filterScaleTracks(rawClip); // 🔸 스케일 트랙 제거
          setCurrentAnimation({ id, animation: filteredClip });
          timeout = setTimeout(() => {
            setAnimationState({
              id,
              state: {
                type: 'Idle',
              },
            });
          }, filteredClip.duration * SECOND);
        } finally {
          //
        }
      }
    };

    setAnimation();

    return () => clearTimeout(timeout);
  }, [animationList, animationState]);

  useEffect(() => {
    if (!mixer.current) return;

    if (currentAnimation) {
      const action = mixer.current.clipAction(currentAnimation);
      const animationFadeDelay = animationState?.type === 'Idle' ? 0 : 1;

      action.reset().fadeIn(animationFadeDelay).play();

      return () => {
        action.fadeOut(1);
      };
    }
  }, [currentAnimation?.uuid]);

  useFrame((_, delta) => {
    mixer?.current?.update(delta);
  });
};

export default useAnimation;
